<template>
	<div class="json-editor-container" :style="{ height: height }">
		<div class="editor-toolbar">
			<div class="toolbar-left">
				<v-btn size="x-small"
					   variant="text"
					   icon="mdi-plus"
					   @click="increaseFontSize"
					   title="Increase font size"></v-btn>
				<v-btn size="x-small"
					   variant="text"
					   icon="mdi-minus"
					   @click="decreaseFontSize"
					   title="Decrease font size"></v-btn>
				<span class="font-size-label">{{ fontSize }}px</span>
				<v-divider vertical class="mx-2"></v-divider>
				<v-btn size="small"
					   variant="text"
					   prepend-icon="mdi-format-align-left"
					   @click="formatJson"
					   :disabled="!isValid"
					   title="Format JSON with compact arrays">
					Format
				</v-btn>
			</div>
			<div class="toolbar-right">
				<v-chip size="small" :color="lineCountColor">
					{{ lineCount }} lines
				</v-chip>
			</div>
		</div>
		<textarea ref="editor"
				  v-model="localValue"
				  @input="handleInput"
				  @keydown="handleKeyDown"
				  @scroll="syncScroll"
				  class="json-editor"
				  :class="{ 'error': !isValid, 'dark': isDark }"
				  :style="editorStyle"
				  spellcheck="false"></textarea>
		<div ref="lineNumbers"
			 class="line-numbers"
			 :class="{ 'dark': isDark }"
			 :style="lineNumberStyle"
			 @scroll="syncScrollFromLineNumbers">
			<div v-for="line in lineCount"
				 :key="line"
				 class="line-number">
				{{ line }}
			</div>
		</div>
		<div v-if="errorMessage"
			 class="error-message"
			 :class="{ 'dark': isDark }">
			<v-icon size="small" color="error">mdi-alert-circle</v-icon>
			{{ errorMessage }}
		</div>
	</div>
</template>

<script>
	export default {
		name: "JsonEditor",
		props: {
			modelValue: {
				type: String,
				default: "",
			},
			height: {
				type: String,
				default: "400px",
			},
		},
		data() {
			return {
				localValue: "",
				fontSize: 14,
				errorMessage: "",
				isValid: true,
			};
		},
		computed: {
			isDark() {
				return this.$vuetify.theme.global.name === "dark";
			},
			lineCount() {
				return this.localValue.split("\n").length;
			},
			lineCountColor() {
				if (this.lineCount > 500) return "error";
				if (this.lineCount > 200) return "warning";
				return "info";
			},
			editorStyle() {
				return {
					fontSize: `${this.fontSize}px`,
					lineHeight: `${this.fontSize * 1.5}px`,
				};
			},
			lineNumberStyle() {
				return {
					fontSize: `${this.fontSize}px`,
					lineHeight: `${this.fontSize * 1.5}px`,
				};
			},
		},
		watch: {
			modelValue(newVal) {
				if (newVal !== this.localValue) {
					// Just set the value as-is, don't reformat
					// The parent component is responsible for formatting
					this.localValue = newVal;
					this.validateJson();
				}
			},
		},
		mounted() {
			// Initialize - just set the value without formatting here
			// Let the watch handle the formatting
			this.localValue = this.modelValue || "";
			this.validateJson();
		},
		methods: {
			handleInput() {
				this.$emit("update:modelValue", this.localValue);
				this.validateJson();
			},
			handleKeyDown(e) {
				// Tab key handling
				if (e.key === "Tab") {
					e.preventDefault();
					const start = e.target.selectionStart;
					const end = e.target.selectionEnd;
					const spaces = "  "; // 2 spaces

					this.localValue =
						this.localValue.substring(0, start) +
						spaces +
						this.localValue.substring(end);

					this.$nextTick(() => {
						e.target.selectionStart = e.target.selectionEnd = start + spaces.length;
					});
				}

				// Auto-close brackets and quotes
				const pairs = {
					"{": "}",
					"[": "]",
					'"': '"',
					"'": "'",
				};

				if (pairs[e.key]) {
					const start = e.target.selectionStart;
					const end = e.target.selectionEnd;

					// Only auto-close if no text is selected and we're not already before the closing character
					if (start === end && this.localValue[start] !== pairs[e.key]) {
						e.preventDefault();
						const closeChar = pairs[e.key];
						this.localValue =
							this.localValue.substring(0, start) +
							e.key +
							closeChar +
							this.localValue.substring(end);

						this.$nextTick(() => {
							e.target.selectionStart = e.target.selectionEnd = start + 1;
						});
					}
				}
			},
			validateJson() {
				try {
					if (this.localValue.trim()) {
						JSON.parse(this.localValue);
						this.isValid = true;
						this.errorMessage = "";
					} else {
						this.isValid = true;
						this.errorMessage = "";
					}
				} catch (e) {
					this.isValid = false;
					// Extract line and column from error message
					const match = e.message.match(/position (\d+)/i);
					if (match) {
						const position = parseInt(match[1]);
						const lines = this.localValue.substring(0, position).split('\n');
						const line = lines.length;
						const column = lines[lines.length - 1].length + 1;
						this.errorMessage = `Line ${line}, Column ${column}: ${e.message}`;
					} else {
						this.errorMessage = e.message;
					}
				}
			},
			increaseFontSize() {
				if (this.fontSize < 24) {
					this.fontSize++;
				}
			},
			decreaseFontSize() {
				if (this.fontSize > 10) {
					this.fontSize--;
				}
			},
			formatJson() {
				if (this.isValid && this.localValue.trim()) {
					const parsed = JSON.parse(this.localValue);
					this.localValue = this.formatJsonWithCompactArrays(parsed);
					this.$emit("update:modelValue", this.localValue);
				}
			},
			formatJsonWithCompactArrays(obj, indent = 0) {
				const spaces = '  '.repeat(indent);
				const nextSpaces = '  '.repeat(indent + 1);

				if (Array.isArray(obj)) {
					// Check if array contains only primitives (no objects/arrays)
					const hasOnlyPrimitives = obj.every(item =>
						typeof item !== 'object' || item === null
					);

					if (hasOnlyPrimitives) {
						// Format array on one line
						return '[' + obj.map(item => JSON.stringify(item)).join(', ') + ']';
					} else {
						// Format array with each item on new line
						const items = obj.map(item =>
							nextSpaces + this.formatJsonWithCompactArrays(item, indent + 1)
						).join(',\n');
						return '[\n' + items + '\n' + spaces + ']';
					}
				} else if (obj !== null && typeof obj === 'object') {
					const keys = Object.keys(obj);
					if (keys.length === 0) return '{}';

					const items = keys.map(key => {
						const value = this.formatJsonWithCompactArrays(obj[key], indent + 1);
						return `${nextSpaces}"${key}": ${value}`;
					}).join(',\n');

					return '{\n' + items + '\n' + spaces + '}';
				} else {
					return JSON.stringify(obj);
				}
			},
			syncScroll() {
				const editor = this.$refs.editor;
				const lineNumbers = this.$refs.lineNumbers;
				if (lineNumbers && editor) {
					lineNumbers.scrollTop = editor.scrollTop;
				}
			},
			syncScrollFromLineNumbers() {
				const editor = this.$refs.editor;
				const lineNumbers = this.$refs.lineNumbers;
				if (lineNumbers && editor) {
					editor.scrollTop = lineNumbers.scrollTop;
				}
			},
		},
	};
</script>

<style scoped>
	.json-editor-container {
		position: relative;
		border: 1px solid #ddd;
		border-radius: 4px;
		overflow: hidden;
		display: flex;
		flex-direction: column;
	}

	.editor-toolbar {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 4px 8px;
		background: #f5f5f5;
		border-bottom: 1px solid #ddd;
	}

		.editor-toolbar.dark {
			background: #1e1e1e;
			border-bottom-color: #333;
		}

	.toolbar-left {
		display: flex;
		align-items: center;
		gap: 4px;
	}

	.font-size-label {
		font-size: 12px;
		color: #666;
		margin-left: 4px;
	}

	.toolbar-right {
		display: flex;
		align-items: center;
	}

	.json-editor {
		flex: 1;
		width: 100%;
		padding: 12px;
		padding-left: 60px;
		font-family: "Consolas", "Monaco", "Courier New", monospace;
		border: none;
		outline: none;
		resize: none;
		background: #ffffff;
		color: #333;
		overflow: auto;
		white-space: pre;
		tab-size: 2;
	}

		.json-editor.dark {
			background: #1e1e1e;
			color: #d4d4d4;
		}

		.json-editor.error {
			border-left: 3px solid #f44336;
		}

	.line-numbers {
		position: absolute;
		left: 0;
		top: 41px;
		width: 50px;
		padding: 12px 8px;
		background: #f9f9f9;
		color: #999;
		text-align: right;
		font-family: "Consolas", "Monaco", "Courier New", monospace;
		user-select: none;
		overflow: hidden;
		border-right: 1px solid #ddd;
	}

		.line-numbers.dark {
			background: #252526;
			color: #858585;
			border-right-color: #333;
		}

	.line-number {
		display: block;
	}

	.error-message {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		padding: 8px 12px;
		background: #ffebee;
		color: #c62828;
		font-size: 12px;
		border-top: 1px solid #ef5350;
		display: flex;
		align-items: center;
		gap: 8px;
	}

		.error-message.dark {
			background: #3d1924;
			color: #ff6b6b;
			border-top-color: #5d2a34;
		}

	/* Syntax highlighting with CSS (basic) */
	.json-editor {
		/* This is a simplified version - for full syntax highlighting,
	   consider using a library like CodeMirror or Monaco Editor */
	}
</style>